import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { z } from 'zod';
import ProgressButton from '@/components/common/progress-button';
import SelectContactList from '@/components/contacts/contact-list/select-contact-list';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useAddFriendsToGroup from '@/hooks/groups/use-add-friends-to-group';
import useGroupMembers from '@/hooks/groups/use-group-members';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';

export default function InviteFriendToGroupRoute() {
  const { id: groupId } = useTypedParams(ROUTES.MESSAGES.GROUPS.SETTINGS.MEMBERS.INVITE);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { members } = useGroupMembers(groupId);
  const { setValue, handleSubmit, watch } = useForm<GroupAddFriendsFormData>({
    resolver: zodResolver(groupAddFriendsFormScheme),
    defaultValues: {
      UserIds: [],
    },
  });
  const userIds = watch('UserIds', []);

  const { mutateAsync: addFriendsToGroup, isPending: isAddingFriendsToGroup } = useAddFriendsToGroup(groupId);

  const handleInviteFriend = async (data: GroupAddFriendsFormData) => {
    if (
      await makePrompt({
        key: 'invite-friend',
        title: t('conversation.setting.group.select-contact'),
        content: t('conversation.setting.group.add-member-confirm'),
      })
    ) {
      const result = await addFriendsToGroup(data.UserIds);

      if (result.Code === 0) {
        navigate(-1);
      }
    }
  };

  return (
    <PageLayout
      title={t('conversation.setting.group.select-contact')}
      className='w-full'
      headerConfig={{
        navigateBack: true,
        headerLabel: t('conversation.setting.group.select-contact'),
        actions: (
          <ProgressButton
            as='button'
            className='btn btn-ghost btn-sm text-primary mode-embedded:text-primary-content'
            isLoading={isAddingFriendsToGroup}
            disabled={!userIds.length}
            onClick={handleSubmit(handleInviteFriend)}
            label='common.confirm'
          />
        ),
      }}>
      <div className='flex flex-1 flex-col items-stretch'>
        <SelectContactList
          onSelectFriends={(ids) => setValue('UserIds', ids)}
          selectedFriendIds={userIds}
          disabledUserIds={Object.keys(members).map(Number)}
        />
      </div>
    </PageLayout>
  );
}

const groupAddFriendsFormScheme = z.object({
  UserIds: z.array(z.number()).min(1),
});

type GroupAddFriendsFormData = z.infer<typeof groupAddFriendsFormScheme>;
